import { useState } from 'react';
import { MercadoLivrePage } from './MercadoLivrePage';
import { ShopeePage } from './ShopeePage';

type MarketplacePlatform = 'mercadolivre' | 'shopee' | 'temu' | 'shein' | 'amazon' | 'aliexpress' | 'magazineluiza' | 'tiktokshop' | 'via' | 'americanas';

interface Marketplace {
  id: MarketplacePlatform;
  name: string;
  status: 'beta' | 'coming_soon';
  icon: React.ReactNode;
  description: string;
}

const MARKETPLACES: Marketplace[] = [
  {
    id: 'mercadolivre',
    name: 'Mercado Livre',
    status: 'beta',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/mercadolivre.png" alt="Mercado Livre" className="w-8 h-8" />
  },
  {
    id: 'shopee',
    name: 'Shopee',
    status: 'beta',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/shopee.png" alt="Shopee" className="w-8 h-8" />
  },
  {
    id: 'temu',
    name: 'Temu',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/temu.png" alt="Temu" className="w-8 h-8" />
  },
  {
    id: 'shein',
    name: 'Shein',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/shein.png" alt="Shein" className="w-8 h-8" />
  },
  {
    id: 'amazon',
    name: 'Amazon',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/amazon.png" alt="Amazon" className="w-8 h-8" />
  },
  {
    id: 'aliexpress',
    name: 'AliExpress',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/aliexpress.png" alt="AliExpress" className="w-8 h-8" />
  },
  {
    id: 'magazineluiza',
    name: 'Magazine Luiza',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/magazineluiza.png" alt="Magazine Luiza" className="w-8 h-8" />
  },
  {
    id: 'tiktokshop',
    name: 'TikTok Shop',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/tiktok.png" alt="TikTok Shop" className="w-8 h-8" />
  },
  {
    id: 'via',
    name: 'Via (Casas Bahia)',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/via.png" alt="Via" className="w-8 h-8" />
  },
  {
    id: 'americanas',
    name: 'Americanas',
    status: 'coming_soon',
    description: 'Conexoes, pedidos e mensagens',
    icon: <img src="/americanas.png" alt="Americanas" className="w-8 h-8" />
  }
];

export function MarketplacesPage() {
  const [selectedPlatform, setSelectedPlatform] = useState<MarketplacePlatform | null>(null);

  if (selectedPlatform === 'mercadolivre') {
    return (
      <div className="space-y-4">
        <button 
          onClick={() => setSelectedPlatform(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-6 pt-6"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Voltar para Marketplaces
        </button>
        <MercadoLivrePage />
      </div>
    );
  }

  if (selectedPlatform === 'shopee') {
    return (
      <div className="space-y-4">
        <button 
          onClick={() => setSelectedPlatform(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-6 pt-6"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Voltar para Marketplaces
        </button>
        <ShopeePage />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketplaces</h1>
        <p className="text-gray-600">
          Conecte contas de marketplace para importar pedidos e gerenciar mensagens em um so lugar.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {MARKETPLACES.map((platform) => (
          <button
            key={platform.id}
            onClick={() => platform.status === 'beta' ? setSelectedPlatform(platform.id) : null}
            className={`flex flex-col items-center p-6 bg-white rounded-xl border transition-all text-center relative ${
              platform.status === 'beta' 
                ? 'border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer' 
                : 'border-gray-100 opacity-70 cursor-not-allowed'
            }`}
          >
            {platform.status === 'beta' ? (
              <span className="absolute top-3 right-3 px-2.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full uppercase tracking-wider">
                BETA
              </span>
            ) : (
              <span className="absolute top-3 right-3 px-2.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-full uppercase tracking-wider">
                EM BREVE
              </span>
            )}
            
            <div className="mb-4">
              {platform.icon}
            </div>
            
            <h3 className="font-semibold text-gray-900 mb-1">{platform.name}</h3>
            <p className="text-xs text-gray-500">{platform.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}


